import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import { validations } from '@unite-us/app-components';
import { InputField } from '@unite-us/ui';
import CurrencyField from 'common/form/CurrencyField';

const AMOUNT_REQUESTED_ID = 'authorization-request-amount-requested';
const AMOUNT_REQUESTED_LABEL = 'Amount Requested';
const AMOUNT_EXCEEDED_CLASS = 'ui-input-field--has-error';

const getAmountFieldClassName = (showAmountExceededError) => (
  showAmountExceededError ? AMOUNT_EXCEEDED_CLASS : ''
);

const buildCurrencyValidations = (isValidationValid) => ([
  { func: (...args) => (isValidationValid() ? validations.isRequired(...args) : null) },
  {
    func: validations.isGreaterThan,
    args: 0,
  },
  {
    func: validations.isLessThan,
    args: 1_000_000_00,
    message: 'Should be less than $1,000,000',
  },
]);

const AuthorizationRequestedAmountField = ({
  isCostBased,
  isReactFinalForm,
  showAmountExceededError,
  registerField,
  fields,
  validateCostBasedAmount,
  isValidationValid,
  validateUnitAmount,
}) => {
  const className = getAmountFieldClassName(showAmountExceededError);

  if (isCostBased && isReactFinalForm) {
    return (
      <Field
        name="authorizationRequest.amount_requested"
        validate={validateCostBasedAmount}
      >
        {({ input, meta }) => (
          <CurrencyField
            className={className}
            input={input}
            meta={meta}
            id={AMOUNT_REQUESTED_ID}
            label={AMOUNT_REQUESTED_LABEL}
            ref={registerField}
            required
          />
        )}
      </Field>
    );
  }

  if (isCostBased) {
    return (
      <CurrencyField
        className={className}
        field={fields.authorizationRequest.amount_requested}
        id={AMOUNT_REQUESTED_ID}
        label={AMOUNT_REQUESTED_LABEL}
        ref={registerField}
        required
        validations={buildCurrencyValidations(isValidationValid)}
      />
    );
  }

  if (isReactFinalForm) {
    return (
      <Field
        name="authorizationRequest.amount_requested"
        validate={(val) => {
          if (!isValidationValid()) return null;
          return validateUnitAmount(val);
        }}
      >
        {({ input, meta }) => (
          <InputField
            className={className}
            id={AMOUNT_REQUESTED_ID}
            label={AMOUNT_REQUESTED_LABEL}
            input={input}
            meta={meta}
            type="number"
            ref={registerField}
            required
          />
        )}
      </Field>
    );
  }

  return (
    <InputField
      className={className}
      id={AMOUNT_REQUESTED_ID}
      label={AMOUNT_REQUESTED_LABEL}
      field={fields.authorizationRequest.amount_requested}
      type="number"
      ref={registerField}
      validations={(...args) => (isValidationValid() ? validateUnitAmount(...args) : null)}
      required
    />
  );
};

AuthorizationRequestedAmountField.propTypes = {
  isCostBased: PropTypes.bool.isRequired,
  isReactFinalForm: PropTypes.bool.isRequired,
  showAmountExceededError: PropTypes.bool.isRequired,
  registerField: PropTypes.func.isRequired,
  fields: PropTypes.object,
  validateCostBasedAmount: PropTypes.func.isRequired,
  isValidationValid: PropTypes.func.isRequired,
  validateUnitAmount: PropTypes.func.isRequired,
};

AuthorizationRequestedAmountField.defaultProps = {
  fields: undefined,
};

export default AuthorizationRequestedAmountField;
